'use client';

import * as React from 'react';
import { motion, type Variants } from 'framer-motion';

import { Icon, IconNames } from '@components/icon';
import { Typography } from '@components/typography';

export type BenefitItem = {
  icon: IconNames;
  title: string;
  body: string;
  /**
   * When true, the icon well uses a sunrise tint instead of the default
   * pool tint. Reserved for one card per grid so the eye has a focal point.
   */
  isAccent?: boolean;
};

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
};

type Props = {
  items: BenefitItem[];
};

export const BenefitsGrid: React.FC<Props> = ({ items }) => (
  <motion.ul
    className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
    variants={containerVariants}
  >
    {items.map((benefit) => (
      <motion.li
        key={benefit.title}
        className="flex flex-col gap-3 rounded-2xl border border-gray-light/70 bg-white p-5 transition-shadow hover:shadow-md"
        variants={itemVariants}
      >
        <span
          className={
            benefit.isAccent
              ? 'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-secondary-main/30 text-primary-main'
              : 'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-pool/60 text-primary-main'
          }
          aria-hidden="true"
        >
          <Icon name={benefit.icon} size="lg" />
        </span>
        <Typography
          component="h3"
          variant="title5"
          className="text-primary-main"
        >
          {benefit.title}
        </Typography>
        <Typography
          component="p"
          variant="body2"
          className="text-primary-main/70"
        >
          {benefit.body}
        </Typography>
      </motion.li>
    ))}
  </motion.ul>
);
